import ESCPOSPrinter from './escpos-printer'

/**
 * Thermal Printer Service for Dekacare
 * Handles both direct ESC/POS printing and fallback to CSS-based printing
 */
class ThermalPrinterService {
  constructor() {
    this.escpos = new ESCPOSPrinter()
    this.isElectron = this.detectElectron()
    this.isNode = typeof window === 'undefined'
    this.printerName = 'XP-80C' // Default printer name
    this.encoding = 'cp1252' // Default encoding for thermal printers
  }

  /**
   * Detect if running in Electron environment
   */
  detectElectron() {
    if (typeof window !== 'undefined') {
      return !!(window.process && window.process.type)
    }
    return false
  }

  /**
   * Set printer configuration
   */
  configure(options = {}) {
    if (options.printerName) {
      this.printerName = options.printerName
    }
    if (options.encoding) {
      this.encoding = options.encoding
    }
  }

  /**
   * Check if direct printing is available
   */
  async isDirectPrintingAvailable() {
    try {
      // Check for Node.js printer modules
      if (this.isNode || this.isElectron) {
        // Try to access printer module (would need to be installed)
        return await this.checkPrinterModule()
      }

      // Check for Web USB API
      if (navigator.usb) {
        return true
      }

      // Check for Web Serial API
      if (navigator.serial) {
        return true
      }

      return false
    } catch (error) {
      console.warn('Direct printing check failed:', error)
      return false
    }
  }

  /**
   * Check if printer module is available (for Electron/Node.js)
   */
  async checkPrinterModule() {
    try {
      // This would require installing a printer module like 'printer' or 'node-printer'
      // Example: npm install printer
      if (this.isElectron) {
        const { ipcRenderer } = window.require('electron')
        return await ipcRenderer.invoke('check-printer-available', this.printerName)
      }
      return false
    } catch (error) {
      return false
    }
  }

  /**
   * Print receipt data using ESC/POS commands
   */
  async printReceipt(receiptData, options = {}) {
    try {
      // Check if direct printing is available
      const canPrintDirect = await this.isDirectPrintingAvailable()

      if (canPrintDirect && options.useDirectPrint !== false) {
        return await this.printDirect(receiptData, options)
      } else {
        // Fallback to CSS-based printing
        return await this.printWithCSS(receiptData, options)
      }
    } catch (error) {
      console.error('Printing failed:', error)
      // Fallback to CSS printing on error
      return await this.printWithCSS(receiptData, options)
    }
  }

  /**
   * Direct ESC/POS printing
   */
  async printDirect(receiptData, options = {}) {
    try {
      // Build ESC/POS command string
      const escposCommands = this.escpos.buildReceipt(receiptData)

      // Print using available method
      if (this.isElectron) {
        return await this.printViaElectron(escposCommands, options)
      } else if (navigator.usb) {
        return await this.printViaUSB(escposCommands, options)
      } else if (navigator.serial) {
        return await this.printViaSerial(escposCommands, options)
      }

      throw new Error('No direct printing method available')
    } catch (error) {
      console.error('Direct printing failed:', error)
      throw error
    }
  }

  /**
   * Print via Electron (requires main process handling)
   */
  async printViaElectron(commands, options = {}) {
    if (!this.isElectron) {
      throw new Error('Not running in Electron environment')
    }

    try {
      const { ipcRenderer } = window.require('electron')
      const result = await ipcRenderer.invoke('print-thermal', {
        printerName: options.printerName || this.printerName,
        commands: commands,
        encoding: options.encoding || this.encoding
      })

      if (result.success) {
        return { success: true, method: 'electron' }
      } else {
        throw new Error(result.error || 'Electron printing failed')
      }
    } catch (error) {
      console.error('Electron printing error:', error)
      throw error
    }
  }

  /**
   * Print via Web USB API
   */
  async printViaUSB(commands, options = {}) {
    try {
      // Request USB device (thermal printer)
      const device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: 0x04b8 }, // Epson
          { vendorId: 0x0519 }, // Citizen
          { vendorId: 0x154f }, // Generic thermal printers
        ]
      })

      await device.open()
      await device.selectConfiguration(1)
      await device.claimInterface(0)

      // Convert string to buffer
      const encoder = new TextEncoder()
      const data = encoder.encode(commands)

      // Send data to printer
      await device.transferOut(1, data)

      // Close connection
      await device.releaseInterface(0)
      await device.close()

      return { success: true, method: 'usb' }
    } catch (error) {
      console.error('USB printing error:', error)
      throw error
    }
  }

  /**
   * Print via Web Serial API
   */
  async printViaSerial(commands, options = {}) {
    try {
      // Request serial port
      const port = await navigator.serial.requestPort()
      await port.open({ baudRate: 9600 })

      const writer = port.writable.getWriter()
      const encoder = new TextEncoder()
      const data = encoder.encode(commands)

      await writer.write(data)
      writer.releaseLock()
      await port.close()

      return { success: true, method: 'serial' }
    } catch (error) {
      console.error('Serial printing error:', error)
      throw error
    }
  }

  /**
   * Fallback to CSS-based printing (current method)
   */
  async printWithCSS(receiptData, options = {}) {
    // This would integrate with your existing $printDirect method
    // Fallback to CSS-based printing when thermal is not available

    // Create a temporary element with ESC/POS data converted to HTML
    const htmlContent = this.convertToHTML(receiptData)

    return {
      success: true,
      method: 'css',
      html: htmlContent,
      fallback: true
    }
  }

  /**
   * Convert receipt data to HTML for CSS printing
   */
  convertToHTML(receiptData) {
    let html = '<div class="thermal-receipt">'

    // Header
    html += '<div class="receipt-header">'
    html += `<div class="facility-name">${receiptData.entityName || 'DEKACARE'}</div>`
    if (receiptData.entityAddress) {
      html += `<div class="facility-address">${receiptData.entityAddress}</div>`
    }
    if (receiptData.entityPhone) {
      html += `<div class="facility-phone">${receiptData.entityPhone}</div>`
    }
    html += '</div>'

    // Separator
    html += '<div class="separator-solid"></div>'

    // Patient info
    html += '<div class="patient-info">'
    html += `<div class="info-row"><span>Waktu:</span><span>${new Date().toLocaleString('id-ID')}</span></div>`
    if (receiptData.norm) {
      html += `<div class="info-row"><span>No. RM:</span><span>${receiptData.norm}</span></div>`
    }
    if (receiptData.patientName) {
      html += `<div class="info-row"><span>Nama:</span><span>${receiptData.patientName}</span></div>`
    }
    if (receiptData.birthDate) {
      html += `<div class="info-row"><span>Tgl. Lahir:</span><span>${receiptData.birthDate}</span></div>`
    }
    html += '</div>'

    // Services
    html += '<div class="separator-dashed"></div>'
    html += '<div class="services-header">LAYANAN</div>'
    html += '<div class="separator-dashed"></div>'

    html += '<div class="services-list">'
    if (receiptData.services && Array.isArray(receiptData.services)) {
      receiptData.services.forEach(service => {
        html += `<div class="service-row">
          <span class="service-name">${service.name || service.key}</span>
          <span class="service-amount">${this.escpos.formatMoney(service.total || service.amount || 0)}</span>
        </div>`
      })
    }
    html += '</div>'

    // Total
    html += '<div class="separator-solid"></div>'
    html += `<div class="total-row">
      <span class="total-label">Grand Total:</span>
      <span class="total-amount">${this.escpos.formatMoney(receiptData.total || 0)}</span>
    </div>`

    // Footer
    html += '<div class="separator-dashed"></div>'
    if (receiptData.doctor) {
      html += '<div class="receipt-footer">'
      html += `<div class="print-date">${new Date().toLocaleDateString('id-ID')}</div>`
      html += `<div class="doctor-name">${receiptData.doctor}</div>`
      html += '</div>'
    }

    html += '<div class="powered-by">Powered By: dekate (www.dekate.id)</div>'
    html += '</div>'

    return html
  }

  /**
   * Print e-resep receipt specifically using buildEResepReceipt
   */
  async printEResep(eresepData, options = {}) {
    try {
      // Check if direct printing is available
      const canPrintDirect = await this.isDirectPrintingAvailable()

      if (canPrintDirect && options.useDirectPrint !== false) {
        return await this.printEResepDirect(eresepData, options)
      } else {
        // Fallback to CSS-based printing
        return await this.printEResepWithCSS(eresepData, options)
      }
    } catch (error) {
      console.error('E-Resep printing failed:', error)
      // Fallback to CSS printing on error
      return await this.printEResepWithCSS(eresepData, options)
    }
  }

  /**
   * Direct ESC/POS E-Resep printing
   */
  async printEResepDirect(eresepData, options = {}) {
    try {
      // Build E-Resep specific ESC/POS command string
      const escposCommands = this.escpos.buildEResepReceipt(eresepData)

      // Print using available method
      if (this.isElectron) {
        return await this.printViaElectron(escposCommands, options)
      } else if (navigator.usb) {
        return await this.printViaUSB(escposCommands, options)
      } else if (navigator.serial) {
        return await this.printViaSerial(escposCommands, options)
      }

      throw new Error('No direct printing method available')
    } catch (error) {
      console.error('Direct E-Resep printing failed:', error)
      throw error
    }
  }

  /**
   * CSS fallback for E-Resep printing
   */
  async printEResepWithCSS(eresepData, options = {}) {
    // Fallback to CSS-based E-Resep printing when thermal is not available

    // Create a temporary element with E-Resep data converted to HTML
    const htmlContent = this.convertEResepToHTML(eresepData)

    return {
      success: true,
      method: 'css',
      html: htmlContent,
      fallback: true
    }
  }

  /**
   * Convert E-Resep data to HTML for CSS printing
   */
  convertEResepToHTML(eresepData) {
    let html = '<div class="thermal-eresep-receipt">'

    // Header
    html += '<div class="receipt-header text-center">'
    html += `<div class="facility-name" style="font-weight: bold; font-size: 1.2em;">${eresepData.entityData?.name || 'DEKACARE'}</div>`
    if (eresepData.entityData?.address) {
      html += `<div class="facility-address">${eresepData.entityData.address}</div>`
    }
    if (eresepData.entityData?.phone) {
      html += `<div class="facility-phone">${eresepData.entityData.phone}</div>`
    }
    html += '</div>'

    // Separator
    html += '<div class="separator-solid" style="border-top: 2px solid #000; margin: 10px 0;"></div>'

    // Title
    html += '<div class="receipt-title text-center" style="font-weight: bold;">'
    if (eresepData.isInvoice && eresepData.noNota) {
      html += `No.Nota: ${eresepData.noNota}`
    } else {
      html += 'Resep Rawat Jalan'
    }
    html += '</div>'

    // Date and location
    if (eresepData.kunjungan?.ruangan?.deskripsi) {
      html += `<div class="text-center">${eresepData.kunjungan.ruangan.deskripsi} `
    } else {
      html += '<div class="text-center">'
    }
    html += `${new Date().toLocaleString('id-ID')}</div>`

    // Patient info
    html += '<div class="separator-dashed" style="border-top: 1px dashed #000; margin: 5px 0;"></div>'
    html += '<div class="patient-info">'

    if (eresepData.kunjungan?.pendaftaran?.pasien?.nama) {
      const patient = eresepData.kunjungan.pendaftaran.pasien
      const norm = eresepData.kunjungan.pendaftaran.norm
      html += `${patient.nama}`
      if (norm) {
        html += ` (No.RM ${norm})`
      }
      if (patient.tanggal_lahir) {
        html += ` - ${this.formatDate(patient.tanggal_lahir)}`
      }
    } else if (eresepData.pasien) {
      html += `${eresepData.pasien.nama}`
      if (eresepData.pasien.no_rm && eresepData.pasien.tglLahir) {
        html += ` (No.RM ${eresepData.pasien.no_rm}) - ${this.formatDate(eresepData.pasien.tglLahir)}`
      }
    } else if (eresepData.isInvoice && eresepData.summary?.nama_pembeli) {
      html += eresepData.summary.nama_pembeli
    } else {
      html += 'Pembeli Langsung.'
    }
    html += '</div>'

    // Medical history (only for non-invoice)
    if (!eresepData.isInvoice) {
      html += '<div class="separator-dashed" style="border-top: 1px dashed #000; margin: 5px 0;"></div>'
      html += '<div class="medical-history">Riwayat</div>'
      html += '<div style="display: flex; justify-content: space-between;">'
      html += `<span>[${eresepData.statusGinjal ? '✓' : ' '}] Gangguan Fungsi Ginjal</span>`
      html += `<span>Hamil [${eresepData.statusHamil ? '✓' : ' '}]</span>`
      html += '</div>'
      html += '<div style="display: flex; justify-content: space-between;">'
      html += `<span>[${eresepData.statusMenyusui ? '✓' : ' '}] Menyusui</span>`
      html += `<span>Cito [${eresepData.statusCito ? '✓' : ' '}]</span>`
      html += '</div>'
      html += '<div class="separator-solid" style="border-top: 1px solid #000; margin: 10px 0;"></div>'
    }

    // Non-racik medicines
    if (eresepData.listObatNonRacik && eresepData.listObatNonRacik.length > 0) {
      if (eresepData.listObatRacik && eresepData.listObatRacik.length > 0) {
        html += '<div class="medicine-section-title">Daftar Obat Non Racik</div>'
      }

      eresepData.listObatNonRacik.forEach(item => {
        html += '<div class="medicine-item">'
        html += `<div style="font-weight: bold;">${item.namaObat}</div>`
        if (item.aturanMinum) {
          html += `<div>${item.aturanMinum}</div>`
        }
        html += '<div style="display: flex; justify-content: space-between;">'
        html += `<span>@${this.escpos.formatMoneySimple(item.hargaSatuan)} x ${item.jumlah}</span>`
        html += `<span>${this.escpos.formatMoneySimple(item.hasilJumlahHarga)}</span>`
        html += '</div>'
        html += '</div>'
      })

      html += '<div class="separator-dashed" style="border-top: 1px dashed #000; margin: 5px 0;"></div>'
      html += '<div style="display: flex; justify-content: space-between; font-weight: bold;">'
      html += `<span>Sub Total</span>`
      html += `<span>${this.escpos.formatMoneySimple(eresepData.grandTotalObatNonRacik || 0)}</span>`
      html += '</div>'
    }

    // Racik medicines
    if (eresepData.listObatRacik && eresepData.listObatRacik.length > 0) {
      if (eresepData.listObatNonRacik && eresepData.listObatNonRacik.length > 0) {
        html += '<div class="separator-dashed" style="border-top: 1px dashed #000; margin: 5px 0;"></div>'
        html += '<div class="medicine-section-title">Daftar Obat Racik</div>'
      }

      eresepData.listObatRacik.forEach(item => {
        html += '<div class="medicine-item">'
        html += `<div>${item.namaRacik}</div>`
        html += '<div style="display: flex; justify-content: space-between;">'
        html += `<span>@${this.escpos.formatMoneySimple(item.hasilJumlahHarga / item.jumlah)} x ${item.jumlah}</span>`
        html += `<span>${this.escpos.formatMoneySimple(item.hasilJumlahHarga)}</span>`
        html += '</div>'
        html += '</div>'
      })

      if (eresepData.listObatNonRacik && eresepData.listObatNonRacik.length > 0) {
        html += '<div class="separator-dashed" style="border-top: 1px dashed #000; margin: 5px 0;"></div>'
        html += '<div style="display: flex; justify-content: space-between; font-weight: bold;">'
        html += `<span>Sub Total</span>`
        html += `<span>${this.escpos.formatMoneySimple(eresepData.grandTotalObatRacik || 0)}</span>`
        html += '</div>'
      }
    }

    // Total
    if (!eresepData.isInvoice && eresepData.listObatNonRacik && eresepData.listObatNonRacik.length > 0) {
      html += '<div class="separator-dashed" style="border-top: 1px dashed #000; margin: 5px 0;"></div>'
      html += '<div style="display: flex; justify-content: space-between; font-weight: bold;">'
      html += `<span>Total</span>`
      html += `<span>${this.escpos.formatMoneySimple(eresepData.subTotal || 0)}</span>`
      html += '</div>'
    }

    // Invoice adjustments
    if (eresepData.isInvoice && eresepData.summary) {
      if (eresepData.summary.ongkos_kirim && parseInt(eresepData.summary.ongkos_kirim) > 0) {
        html += '<div style="display: flex; justify-content: space-between; font-weight: bold;">'
        html += `<span>Ongkos Kirim</span>`
        html += `<span>${this.escpos.formatMoneySimple(eresepData.summary.ongkos_kirim)}</span>`
        html += '</div>'
      }
      if (eresepData.summary.biaya_layanan && parseInt(eresepData.summary.biaya_layanan) > 0) {
        html += '<div style="display: flex; justify-content: space-between; font-weight: bold;">'
        html += `<span>Biaya Layanan</span>`
        html += `<span>${this.escpos.formatMoneySimple(eresepData.summary.biaya_layanan)}</span>`
        html += '</div>'
      }
      if (eresepData.summary.diskon_total && parseInt(eresepData.summary.diskon_total) > 0) {
        html += '<div style="display: flex; justify-content: space-between; font-weight: bold;">'
        html += `<span>Diskon</span>`
        html += `<span>${this.escpos.formatMoneySimple(eresepData.summary.diskon_total)}</span>`
        html += '</div>'
      }
      if (eresepData.summary.pembulatan && parseInt(eresepData.summary.pembulatan) !== 0) {
        html += '<div style="display: flex; justify-content: space-between; font-weight: bold;">'
        html += `<span>Pembulatan</span>`
        html += `<span>${this.escpos.formatMoneySimple(eresepData.summary.pembulatan)}</span>`
        html += '</div>'
      }
    }

    // Grand total
    html += '<div class="separator-solid" style="border-top: 2px solid #000; margin: 10px 0;"></div>'
    html += '<div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 1.2em;">'
    html += `<span>Grand Total</span>`
    html += `<span>${this.escpos.formatMoneySimple(eresepData.grandTotalWithAdjustments || 0)}</span>`
    html += '</div>'

    // Payment info (for invoice)
    if (eresepData.isInvoice && eresepData.summary) {
      if (eresepData.summary.nominal_bayar && eresepData.summary.payment_method) {
        html += '<div style="display: flex; justify-content: space-between;">'
        html += `<span>Dibayar: ${eresepData.summary.payment_method}</span>`
        html += `<span>${this.escpos.formatMoneySimple(eresepData.summary.nominal_bayar)}</span>`
        html += '</div>'
      }
      if (eresepData.summary.nominal_bayar && eresepData.kembalian !== undefined) {
        html += '<div style="display: flex; justify-content: space-between;">'
        html += `<span>Kembalian</span>`
        html += `<span>${this.escpos.formatMoneySimple(eresepData.kembalian)}</span>`
        html += '</div>'
      }
    }

    // Signatures (only for non-invoice)
    if (!eresepData.isInvoice) {
      html += '<div class="separator-dashed" style="border-top: 1px dashed #000; margin: 5px 0;"></div>'
      html += '<div style="display: flex; justify-content: space-between; text-align: center;">'
      html += '<span>Petugas</span><span>Penerima</span>'
      html += '</div>'
      html += '<br><br><br>'

      if (eresepData.userData?.name_with_title) {
        html += '<div style="display: flex; justify-content: space-between;">'
        html += `<span>(${eresepData.userData.name_with_title})</span><span>(          )</span>`
        html += '</div>'
      } else {
        html += '<div style="display: flex; justify-content: space-between;">'
        html += '<span>(          )</span><span>(          )</span>'
        html += '</div>'
      }
    }

    // Footer
    if (eresepData.footerText) {
      html += '<div class="separator-solid" style="border-top: 1px solid #000; margin: 10px 0;"></div>'
      html += `<div class="text-center">${eresepData.footerText}</div>`
    }

    html += '<div class="separator-solid" style="border-top: 1px solid #000; margin: 10px 0;"></div>'
    html += '<div class="text-center">Powered By: dekate (www.dekate.id)</div>'
    html += '<div class="text-center">Str e-POS</div>'

    html += '</div>'

    return html
  }

  /**
   * Helper function to format date
   */
  formatDate(date) {
    if (!date) return ''

    // Handle DD-MM-YYYY format
    if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const parts = date.split('-')
      const dateObj = new Date(parts[2], parts[1] - 1, parts[0])
      return dateObj.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    }

    // Handle other date formats
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return ''

    return dateObj.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  /**
   * Test printer connection
   */
  async testPrint() {
    const testData = {
      entityName: 'DEKACARE TEST',
      entityAddress: 'Test Print Function',
      entityPhone: 'Test - ' + new Date().toLocaleTimeString(),
      services: [
        { name: 'Test Service', amount: 50000 }
      ],
      total: 50000,
      qrData: 'Test QR Code Data'
    }

    return await this.printReceipt(testData, { useDirectPrint: true })
  }
}

export default ThermalPrinterService